import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { BillingAlert, UserInterface } from 'src/app/models/user.interface';
import { UserService } from 'src/app/core/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'globot-pay-notification',
  templateUrl: './pay-notification.component.html',
  styleUrls: ['./pay-notification.component.scss'],
})
export class PayNotificationComponent implements OnInit, OnChanges {
  @Input() showCloseButton: boolean = true;
  /** Muestra el botón de acción (Ver mi plan / Actualizar método de pago). */
  @Input() showCtaButton: boolean = true;
  /** Si es false, no se puede cerrar ni se persiste dismiss en localStorage. */
  @Input() allowDismiss: boolean = true;
  @Input() flexDirection: 'row' | 'column' = 'row';
  /** Si es true, usa el usuario pasado por @Input user en lugar de llamar GET /user */
  @Input() useUserFromParent = false;
  @Input() user: UserInterface | null = null;

  options = {
    path: '/assets/lotties/bell.json',
  };
  visible: boolean = false;
  billingAlert: BillingAlert | null = null;
  subscriptionsEnabled = environment.subscriptionsEnabled;

  constructor(private userService: UserService, private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.useUserFromParent && changes['user'] && this.user) {
      this.applyBillingAlert(this.user);
    }
  }

  ngOnInit(): void {
    if (!this.subscriptionsEnabled) {
      return;
    }
    if (this.useUserFromParent) {
      if (this.user) {
        this.applyBillingAlert(this.user);
      }
      return;
    }
    this.userService.getUser().subscribe({
      next: (user: UserInterface) => {
        this.applyBillingAlert(user);
      },
      error: (err) => {
        console.error('Error al obtener los datos del usuario:', err);
      },
    });
  }

  private applyBillingAlert(user: UserInterface): void {
    this.billingAlert = user.billing_alert ?? null;
    if (this.billingAlert?.show && this.billingAlert.type) {
      if (this.allowDismiss) {
        const dismissKey = this.getDismissKey();
        if (dismissKey && localStorage.getItem(dismissKey) === 'true') {
          this.visible = false;
          return;
        }
      }
      this.visible = true;
    } else {
      this.visible = false;
    }
  }

  private getDismissKey(): string | null {
    if (!this.billingAlert?.type) {
      return null;
    }
    const endDate = this.billingAlert.end_date ?? '';
    return `pay_notification_dismissed_${this.billingAlert.type}_${endDate}`;
  }

  get title(): string {
    if (this.billingAlert?.message) {
      return this.billingAlert.message;
    }
    if (this.billingAlert?.type === 'payment_failed') {
      return 'No pudimos procesar tu pago';
    }
    return '¡Es hora de renovar tu suscripción!';
  }

  get subtitle(): string | null {
    if (this.billingAlert?.type === 'payment_failed') {
      const attempts = this.billingAlert?.failed_attempts;
      const max = this.billingAlert?.max_attempts ?? 3;
      if (attempts != null) {
        return `Intento ${attempts} de ${max}. Actualiza tu método de pago para evitar la suspensión.`;
      }
      return 'Actualiza tu método de pago para continuar con tu plan.';
    }
    // renewal_due: el mensaje principal ya viene en billing_alert.message (title)
    return null;
  }

  get ctaLabel(): string {
    const cta = this.billingAlert?.cta;
    if (cta === 'update_payment_method') {
      return 'Actualizar método de pago';
    }
    if (cta === 'view_plan') {
      return 'Ver mi plan';
    }
    if (this.billingAlert?.type === 'payment_failed') {
      return 'Actualizar método de pago';
    }
    return 'Ver mi plan';
  }

  closeNotification(): void {
    if (!this.allowDismiss) {
      return;
    }
    const dismissKey = this.getDismissKey();
    if (dismissKey) {
      localStorage.setItem(dismissKey, 'true');
    }
    this.visible = false;
  }

  get showActions(): boolean {
    return this.showCtaButton || this.showCloseButton;
  }

  onCtaClick(): void {
    this.router.navigate(['/dashboard', 'edit-user'], { queryParams: { tab: 'plan' } });
  }
}
